/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function, no-empty-function, max-lines */
import { useMutation } from "@apollo/client";
import usePrevious from "@bluframe/grapple/usePrevious";
import { mutations } from "@bluframe/mightychroma-mightytool-graphql-schema";
import equals from "ramda/src/equals";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import reducer, { DEFAULT_HOME_VIDEO_REVIEW, initialState, RESET_SELECTED_REVIEW, RESET_SELECTED_REVIEWS, SET_HOME_VIDEO_ID, SET_IMAGES, SET_IMDB_ID, SET_IS_ADD_HOME_VIDEO, SET_IS_ALL_AROUND_GATHER, SET_IS_MATCH_TITLE, SET_IS_ONLY_NEW, SET_IS_ONLY_PRESENT, SET_IS_SHOW_FLAGGED, SET_IS_TOGGLE_FLAGGED_LOADING, SET_IS_UHD, SET_LIST, SET_NAME, SET_NEXT_CURSOR, SET_REVIEW, SET_SCREENSHOTS, SET_SELECTED_HOME_VIDEO_REVIEW, SET_SELECTED_ID, SET_SELECTED_IS_PREVIEW_LOADING, SET_SELECTED_IS_PREVIEW_VALID, SET_SELECTED_IS_REVIEW_CREATED, SET_TECH_SPECS, SET_TMDB_SELECTED_ID, SET_TMDB_SELECTED_IMDB_ID } from "./reducer";
const { critics: { VALIDATE_PREVIEW } } = mutations;
export { DEFAULT_HOME_VIDEO_REVIEW };
export const initialContext = {
    clear: () => { },
    clearAll: () => { },
    resetSelectedReviews: () => { },
    setHomeVideoId: () => { },
    setImages: () => { },
    setScreenshots: () => { },
    setIsAddHomeVideo: () => { },
    setIsAllAroundGather: () => { },
    setIsMatchTitle: () => { },
    setIsOnlyNew: () => { },
    setIsOnlyPresent: () => { },
    setIsShowFlagged: () => { },
    setIsToggleFlaggedLoading: () => { },
    setIsUHD: () => { },
    setList: () => { },
    setName: () => { },
    setNextCursor: () => { },
    setIMDbId: () => { },
    setTmdbSelectedImdbId: () => { },
    setTmdbSelectedId: () => { },
    setReview: () => { },
    setSelectedHomeVideoReview: () => { },
    setSelectedId: () => { },
    setSelectedIsPreviewLoading: () => { },
    setSelectedIsPreviewValid: () => { },
    setSelectedIsReviewCreated: () => { },
    setTechSpecs: () => { },
    state: initialState
};
const useReviewsContext = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const previousState = usePrevious(state);
    const [validatePreview] = useMutation(VALIDATE_PREVIEW);
    /**
     * resetSelectedReviews
     *
     * @description Reset the selected reviews list
     * @param {void}
     * @returns {void}
     *
     */
    const resetSelectedReviews = useCallback(() => {
        dispatch({ type: RESET_SELECTED_REVIEWS });
    }, [dispatch]);
    /**
     * setHomeVideoId
     *
     * @description Set the home video ID for a specific service
     * @param {string | undefined} homeVideoId - The home video ID to set
     * @returns {void}
     *
     */
    const setHomeVideoId = useCallback((homeVideoId) => {
        dispatch({
            payload: { homeVideoId },
            type: SET_HOME_VIDEO_ID
        });
    }, [dispatch]);
    /**
     * setImages
     *
     *  @description Set the image URL for a specific service
     *  @param {ImageAddInput[]} images - The images to set
     *  @returns {void}
     *
     */
    const setImages = useCallback((images) => {
        dispatch({
            payload: { images },
            type: SET_IMAGES
        });
    }, [dispatch]);
    const setScreenshots = useCallback((screenshots) => {
        dispatch({
            payload: { screenshots },
            type: SET_SCREENSHOTS
        });
    }, [dispatch]);
    /**
     * setIsAddHomeVideo
     *
     * @description Set the isAddHomeVideo flag for a specific service
     * @param {boolean} isAddHomeVideo - The isAddHomeVideo flag to set
     * @returns {void}
     *
     */
    const setIsAddHomeVideo = useCallback((isAddHomeVideo) => {
        dispatch({
            payload: { isAddHomeVideo },
            type: SET_IS_ADD_HOME_VIDEO
        });
    }, [dispatch]);
    /**
     * setIsAllAroundGather
     *
     * @description Set the isAllAroundGather flag for a specific service
     * @param {boolean} isAllAroundGather - The isAllAroundGather flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setIsAllAroundGather = useCallback((isAllAroundGather, serviceName) => {
        dispatch({
            payload: { isAllAroundGather, service: serviceName },
            type: SET_IS_ALL_AROUND_GATHER
        });
    }, [dispatch]);
    /**
     * setIsMatchTitle
     *
     * @description Set the isMatchTitle flag
     * @param {boolean} isMatchTitle - The isMatchTitle flag to set
     * @returns {void}
     *
     */
    const setIsMatchTitle = useCallback((isMatchTitle) => {
        dispatch({ payload: { isMatchTitle }, type: SET_IS_MATCH_TITLE });
    }, [dispatch]);
    /**
     * setIsOnlyNew
     *
     * @description Set the isOnlyNew flag for a specific service
     * @param {boolean} isOnlyNew - The isOnlyNew flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setIsOnlyNew = useCallback((isOnlyNew, serviceName) => {
        dispatch({
            payload: { isOnlyNew, service: serviceName },
            type: SET_IS_ONLY_NEW
        });
    }, [dispatch]);
    /**
     * setIsOnlyPresent
     *
     * @description Set the isOnlyPresent flag for a specific service
     * @param {boolean} isOnlyPresent - The isOnlyPresent flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setIsOnlyPresent = useCallback((isOnlyPresent, serviceName) => {
        dispatch({
            payload: { isOnlyPresent, service: serviceName },
            type: SET_IS_ONLY_PRESENT
        });
    }, [dispatch]);
    /**
     * setIsShowFlagged
     *
     * @description Set the isShowFlagged flag for a specific service
     * @param {boolean} isShowFlagged - The isShowFlagged flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setIsShowFlagged = useCallback((isShowFlagged, serviceName) => {
        dispatch({
            payload: { isShowFlagged, service: serviceName },
            type: SET_IS_SHOW_FLAGGED
        });
    }, [dispatch]);
    /**
     * setIsToggleFlaggedLoading
     *
     * @description Set the isToggleFlaggedLoading flag
     * @param {boolean} isToggleFlaggedLoading - The isToggleFlaggedLoading flag to set
     * @returns {void}
     *
     */
    const setIsToggleFlaggedLoading = useCallback((isToggleFlaggedLoading) => {
        dispatch({
            payload: { isToggleFlaggedLoading },
            type: SET_IS_TOGGLE_FLAGGED_LOADING
        });
    }, [dispatch]);
    /**
     * setIsUHD
     *
     * @description Set the isUHD flag
     * @param {boolean} isUHD - The isUHD flag to set
     * @returns {void}
     *
     */
    const setIsUHD = useCallback((isUHD) => {
        dispatch({
            payload: { isUHD },
            type: SET_IS_UHD
        });
    }, [dispatch]);
    /**
     * setList
     *
     * @description Set the list of reviews for a specific service
     * @param {Review[]} list - The list of reviews to set
     * @param {ReviewsServiceName} serviceName - The service name to set the list for
     * @returns {void}
     *
     */
    const setList = useCallback((list, serviceName) => {
        dispatch({
            payload: { list, service: serviceName },
            type: SET_LIST
        });
    }, [dispatch]);
    /**
     * setName
     *
     * @description Set the name for a specific service
     * @param {string} name - The name to set
     * @returns {void}
     *
     */
    const setName = useCallback((name) => {
        dispatch({ payload: { name }, type: SET_NAME });
    }, [dispatch]);
    /**
     * setNextCursor
     *
     * @description Set the next cursor for a specific service
     * @param {string | null} nextCursor - The next cursor to set
     * @param {ReviewsServiceName} service - The service name to set the cursor for
     * @returns {void}
     *
     */
    const setNextCursor = useCallback((nextCursor, service) => {
        dispatch({
            payload: { nextCursor, service },
            type: SET_NEXT_CURSOR
        });
    }, [dispatch]);
    /**
     * setImdbId
     *
     * @description Set the IMDb ID for a specific service
     * @param {string} IMDbId - The IMDb ID to set
     * @returns {void}
     *
     */
    const setIMDbId = useCallback((IMDbId) => {
        dispatch({
            payload: { IMDbId },
            type: SET_IMDB_ID
        });
    }, [dispatch]);
    /**
     * setTmdbSelectedImdbId
     *
     * @description Set the TMDB selected IMDb ID
     * @param {string} tmdbSelectedImdbId - The TMDB selected IMDb ID to set
     * @returns {void}
     *
     */
    const setTmdbSelectedImdbId = useCallback((tmdbSelectedImdbId) => {
        dispatch({
            payload: { tmdbSelectedImdbId },
            type: SET_TMDB_SELECTED_IMDB_ID
        });
    }, [dispatch]);
    /**
     * setTmdbSelectedId
     *
     * @description Set the TMDB selected ID (the TMDB movie/show ID)
     * @param {string} tmdbSelectedId - The TMDB ID to set
     * @returns {void}
     *
     */
    const setTmdbSelectedId = useCallback((tmdbSelectedId) => {
        dispatch({
            payload: { tmdbSelectedId },
            type: SET_TMDB_SELECTED_ID
        });
    }, [dispatch]);
    /**
     * setReview
     *
     * @description Set the review for a specific service
     * @param {SelectedReview["review"]} review - The review to set
     * @param {ReviewsServiceName} serviceName - The service name to set the review for
     * @returns {void}
     *
     */
    const setReview = useCallback((review, serviceName) => {
        dispatch({
            payload: { review, serviceName },
            type: SET_REVIEW
        });
    }, [dispatch]);
    /**
     * setSelectedHomeVideoReview
     *
     * @description Set the selected home video review for a specific service
     * @param {SetSelectedHomeVideoReviewProps} review - The review to set
     * @returns {void}
     *
     */
    const setSelectedHomeVideoReview = useCallback(({ id, isCreated, isPreviewValid, isPublished, serviceName, title, url }) => {
        dispatch({
            payload: {
                id,
                isCreated,
                isPreviewValid,
                isPublished,
                service: serviceName,
                title,
                url
            },
            type: SET_SELECTED_HOME_VIDEO_REVIEW
        });
    }, [dispatch]);
    /**
     * setSelectedId
     *
     * @description Set the selected ID for a specific service
     * @param {string} selected - The selected ID to set
     * @param {ReviewsServiceName} serviceName - The service name to set the ID for
     * @returns {void}
     *
     */
    const setSelectedId = useCallback((selected, serviceName) => {
        dispatch({
            payload: { selected, service: serviceName },
            type: SET_SELECTED_ID
        });
    }, [dispatch]);
    /**
     * clear
     *
     * @description Clear the selected reviews critics for a specific service
     * @param {ReviewsServiceName} serviceName - The service name to clear
     * @returns {void}
     *
     */
    const clear = useCallback((serviceName) => {
        dispatch({
            payload: { service: serviceName },
            type: RESET_SELECTED_REVIEW
        });
    }, [dispatch]);
    /**
     * clearAll
     *
     * @description Clear all selected reviews critics
     * @param {void}
     * @returns {void}
     *
     */
    const clearAll = useCallback(() => {
        resetSelectedReviews();
        setIMDbId("");
        setTmdbSelectedImdbId(undefined);
    }, [resetSelectedReviews, setIMDbId, setTmdbSelectedImdbId]);
    /**
     * setSelectedIsPreviewLoading
     *
     * @description Set the isPreviewLoading flag for a specific service
     * @param {boolean} isPreviewLoading - The isPreviewLoading flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setSelectedIsPreviewLoading = useCallback((isPreviewLoading, serviceName) => {
        dispatch({
            payload: { isPreviewLoading, service: serviceName },
            type: SET_SELECTED_IS_PREVIEW_LOADING
        });
    }, [dispatch]);
    /**
     * setSelectedIsPreviewValid
     *
     * @description Set the isPreviewValid flag for a specific service
     * @param {boolean} isPreviewValid - The isPreviewValid flag to set
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setSelectedIsPreviewValid = useCallback((isPreviewValid, serviceName) => {
        dispatch({
            payload: { isPreviewValid, service: serviceName },
            type: SET_SELECTED_IS_PREVIEW_VALID
        });
    }, [dispatch]);
    /**
     * setSelectedIsReviewCreated
     *
     * @description Set the isReviewCreated flag for a specific service
     * @param {string} id - The ID of the review
     * @param {ReviewsServiceName} serviceName - The service name to set the flag for
     * @returns {void}
     *
     */
    const setSelectedIsReviewCreated = useCallback((id, serviceName) => {
        dispatch({
            payload: { id, service: serviceName },
            type: SET_SELECTED_IS_REVIEW_CREATED
        });
    }, [dispatch]);
    /**
     * setTechSpecs
     *
     * @description Set the tech specs for a specific service
     * @param {TechSpecIdentifier[][]} techSpecs - The tech specs to set
     * @returns {void}
     *
     */
    const setTechSpecs = useCallback((techSpecs) => {
        dispatch({
            payload: { techSpecs },
            type: SET_TECH_SPECS
        });
    }, [dispatch]);
    /**
     * checkAndValidateReview
     *
     * @description Check and validate the review for a specific service
     * @param {ReviewsServiceName} serviceName - The service name to check and validate
     * @returns {void}
     *
     */
    const checkAndValidateReview = useCallback((serviceName) => {
        const { review: preparedReview } = state[serviceName].selected;
        const serviceState = state[serviceName];
        const previousServiceState = previousState?.[serviceName];
        if (!preparedReview ||
            !serviceState.selected.url ||
            equals(serviceState.selected.review, previousServiceState?.selected.review) ||
            serviceState.selected.isPreviewLoading ||
            equals(serviceState.selected.review, {}))
            return;
        setSelectedIsPreviewLoading(true, serviceName);
        validatePreview({
            onCompleted: (data) => {
                setSelectedIsPreviewLoading(false, serviceName);
                setSelectedIsPreviewValid(data.validatePreview.isValid, data.validatePreview.serviceName);
            },
            onError: () => {
                setSelectedIsPreviewLoading(false, serviceName);
                setSelectedIsPreviewValid(false, serviceName);
            },
            variables: {
                previewValidateInput: {
                    ...preparedReview,
                    author: preparedReview.author ?? undefined,
                    editions: preparedReview.editions ?? undefined,
                    images: preparedReview.images ?? undefined,
                    releaseDate: preparedReview.releaseDate ?? undefined,
                    serviceName,
                    techSpecs: preparedReview.techSpecs ?? undefined,
                    url: state[serviceName].selected.url
                }
            }
        });
    }, [
        previousState,
        setSelectedIsPreviewLoading,
        setSelectedIsPreviewValid,
        state,
        validatePreview
    ]);
    useEffect(() => {
        checkAndValidateReview("avForums");
    }, [checkAndValidateReview, state.avForums.selected.review]);
    useEffect(() => {
        checkAndValidateReview("avNirvana");
    }, [checkAndValidateReview, state.avNirvana.selected.review]);
    useEffect(() => {
        checkAndValidateReview("avsForum");
    }, [checkAndValidateReview, state.avsForum.selected.review]);
    useEffect(() => {
        checkAndValidateReview("bluRay");
    }, [checkAndValidateReview, state.bluRay.selected.review]);
    useEffect(() => {
        checkAndValidateReview("bluRayAuthority");
    }, [checkAndValidateReview, state.bluRayAuthority.selected.review]);
    useEffect(() => {
        checkAndValidateReview("digitalBits");
    }, [checkAndValidateReview, state.digitalBits.selected.review]);
    useEffect(() => {
        checkAndValidateReview("doBlu");
    }, [checkAndValidateReview, state.doBlu.selected.review]);
    useEffect(() => {
        checkAndValidateReview("hidefDigest");
    }, [checkAndValidateReview, state.hidefDigest.selected.review]);
    useEffect(() => {
        checkAndValidateReview("highDefDiscNews");
    }, [checkAndValidateReview, state.highDefDiscNews.selected.review]);
    useEffect(() => {
        checkAndValidateReview("homeTheaterForum");
    }, [checkAndValidateReview, state.homeTheaterForum.selected.review]);
    useEffect(() => {
        checkAndValidateReview("theaterByte");
    }, [checkAndValidateReview, state.theaterByte.selected.review]);
    useEffect(() => {
        checkAndValidateReview("whySoBlu");
    }, [checkAndValidateReview, state.whySoBlu.selected.review]);
    return useMemo(() => ({
        clear,
        clearAll,
        resetSelectedReviews,
        setHomeVideoId,
        setImages,
        setScreenshots,
        setIsAddHomeVideo,
        setIsAllAroundGather,
        setIsMatchTitle,
        setIsOnlyNew,
        setIsOnlyPresent,
        setIsShowFlagged,
        setIsToggleFlaggedLoading,
        setIsUHD,
        setList,
        setName,
        setNextCursor,
        setIMDbId,
        setTmdbSelectedImdbId,
        setTmdbSelectedId,
        setReview,
        setSelectedHomeVideoReview,
        setSelectedId,
        setSelectedIsPreviewLoading,
        setSelectedIsPreviewValid,
        setSelectedIsReviewCreated,
        setTechSpecs,
        state
    }), [
        clear,
        clearAll,
        resetSelectedReviews,
        setHomeVideoId,
        setImages,
        setScreenshots,
        setIsAddHomeVideo,
        setIsAllAroundGather,
        setIsMatchTitle,
        setIsOnlyNew,
        setIsOnlyPresent,
        setIsShowFlagged,
        setIsToggleFlaggedLoading,
        setIsUHD,
        setList,
        setName,
        setNextCursor,
        setIMDbId,
        setTmdbSelectedImdbId,
        setReview,
        setSelectedHomeVideoReview,
        setSelectedId,
        setSelectedIsPreviewLoading,
        setSelectedIsPreviewValid,
        setSelectedIsReviewCreated,
        setTechSpecs,
        state
    ]);
};
export default useReviewsContext;
